import type { FieldInstanceValueFields, TableFieldInstanceFields } from "@regrello/graphql-api";

import { getErrorMessageWithPayload } from "../../../../../../utils/getErrorMessageWithPayload";

export function extractAtMostOneTabularArrayOrThrow<
  TReturnValue,
  TTabularValue extends Array<{ value: TReturnValue; rowNumber: number }>,
  TFieldInstanceValueTypeName extends FieldInstanceValueFields["__typename"],
  TFieldInstanceValue extends Extract<FieldInstanceValueFields, { __typename: TFieldInstanceValueTypeName }>,
>(params: {
  fieldInstance: TableFieldInstanceFields;
  fieldInstanceValueTypeName: TFieldInstanceValueTypeName | TFieldInstanceValueTypeName[];
  errorMessageIfMultipleValues: string;
  errorMessageIfWrongValueType: string;
  getterIfNoValue: () => TTabularValue;
  getterIfTabularValue: (fieldInstanceValue: TFieldInstanceValue) => TTabularValue;
}): TTabularValue {
  const {
    fieldInstance,
    fieldInstanceValueTypeName,
    errorMessageIfMultipleValues,
    errorMessageIfWrongValueType,
    getterIfNoValue,
    getterIfTabularValue,
  } = params;

  const { values } = fieldInstance;

  if (values.length > 1) {
    throw new Error(getErrorMessageWithPayload(errorMessageIfMultipleValues, { fieldInstance }));
  }

  if (values.length === 0) {
    return getterIfNoValue();
  }

  const representativeSingleValue = values[0];

  const permittedTypeNames =
    typeof fieldInstanceValueTypeName === "string" ? [fieldInstanceValueTypeName] : fieldInstanceValueTypeName;

  if (!permittedTypeNames.includes(representativeSingleValue.__typename as TFieldInstanceValueTypeName)) {
    throw new Error(getErrorMessageWithPayload(errorMessageIfWrongValueType, { fieldInstance }));
  }

  // (clewis): TS isn't smart enough to know that the type has been narrowed properly based on the
  // __typename, but it works as expected in practice.
  //
  return getterIfTabularValue(representativeSingleValue as TFieldInstanceValue);
}
